import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services';
import type { PaySchedule } from '@/types';

/** Admin: creates a new driver/admin account. There is no self-registration. */
export const createUser = async (
  email: string,
  password: string,
  displayName: string,
): Promise<{ uid: string }> => {
  const callable = httpsCallable<
    { email: string; password: string; displayName: string },
    { uid: string }
  >(functions, 'createUser');
  const { data } = await callable({ email, password, displayName });
  return data;
};

/** Admin: promotes/revokes another user's admin role. */
export const setAdminRole = async (
  uid: string,
  isAdmin: boolean,
): Promise<{ uid: string; isAdmin: boolean }> => {
  const callable = httpsCallable<
    { uid: string; isAdmin: boolean },
    { uid: string; isAdmin: boolean }
  >(functions, 'setAdminRole');
  const { data } = await callable({ uid, isAdmin });
  return data;
};

/** Admin: disables (blocks sign-in) or re-enables a driver account. */
export const setUserDisabled = async (
  uid: string,
  disabled: boolean,
): Promise<{ uid: string; disabled: boolean }> => {
  const callable = httpsCallable<
    { uid: string; disabled: boolean },
    { uid: string; disabled: boolean }
  >(functions, 'setUserDisabled');
  const { data } = await callable({ uid, disabled });
  return data;
};

/** Admin: changes a user's monthly reporting schedule. */
export const setUserPaySchedule = async (
  uid: string,
  paySchedule: PaySchedule,
): Promise<{ uid: string; paySchedule: PaySchedule }> => {
  const callable = httpsCallable<
    { uid: string; paySchedule: PaySchedule },
    { uid: string; paySchedule: PaySchedule }
  >(functions, 'setUserPaySchedule');
  const { data } = await callable({ uid, paySchedule });
  return data;
};

/**
 * Admin: permanently removes a driver's account. Their Auth login is deleted
 * outright and any routes they currently hold are unassigned, but their
 * assignment history is preserved.
 */
export const deleteUser = async (uid: string): Promise<{ uid: string }> => {
  const callable = httpsCallable<{ uid: string }, { uid: string }>(functions, 'deleteUser');
  const { data } = await callable({ uid });
  return data;
};

/**
 * Admin: sets a new password for a driver's account and flags it so they're
 * forced to choose their own new password on next sign-in.
 */
export const resetUserPassword = async (
  uid: string,
  newPassword: string,
): Promise<{ uid: string }> => {
  const callable = httpsCallable<{ uid: string; newPassword: string }, { uid: string }>(
    functions,
    'resetUserPassword',
  );
  const { data } = await callable({ uid, newPassword });
  return data;
};
